import React, { useState, useEffect } from 'react';
import { 
  Wallet, CheckCircle, Clock, XCircle, ArrowRight, 
  RefreshCcw, Search, Filter, Euro, TrendingUp, User,
  Mail, Building
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Textarea } from '../../components/ui/textarea';
import { adminApi } from '../../lib/api';
import { formatDate, formatPrice } from '../../lib/utils';
import { toast } from 'sonner';

const AdminPayoutsPage = () => {
  const [payouts, setPayouts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    fetchData();
  }, [filter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const statusFilter = filter === 'all' ? null : filter;
      const [payoutsRes, statsRes] = await Promise.all([
        adminApi.getPayouts(statusFilter),
        adminApi.getPayoutStats()
      ]);
      setPayouts(payoutsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des versements');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async () => {
    if (!selectedPayout || !newStatus) return;
    
    setUpdating(true);
    try {
      await adminApi.updatePayout(selectedPayout.id, {
        status: newStatus,
        admin_notes: adminNotes || undefined
      });
      toast.success(`Versement mis à jour: ${getStatusLabel(newStatus)}`);
      setShowConfirmDialog(false);
      setSelectedPayout(null);
      setNewStatus('');
      setAdminNotes('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setUpdating(false);
    }
  };

  const openConfirmDialog = (payout, status) => {
    setSelectedPayout(payout);
    setNewStatus(status);
    setAdminNotes('');
    setShowConfirmDialog(true);
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'En attente',
      processing: 'En cours',
      completed: 'Versé',
      cancelled: 'Annulé'
    };
    return labels[status] || status;
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: <Clock className="w-4 h-4 text-amber-500" />,
      processing: <RefreshCcw className="w-4 h-4 text-blue-500 animate-spin" />,
      completed: <CheckCircle className="w-4 h-4 text-green-500" />,
      cancelled: <XCircle className="w-4 h-4 text-red-500" />
    };
    return icons[status] || null;
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-amber-100 text-amber-700 border-amber-200',
      processing: 'bg-blue-100 text-blue-700 border-blue-200',
      completed: 'bg-green-100 text-green-700 border-green-200',
      cancelled: 'bg-red-100 text-red-700 border-red-200'
    };
    return colors[status] || 'bg-slate-100 text-slate-700';
  };

  const filteredPayouts = payouts.filter(payout => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      payout.carrier_name?.toLowerCase().includes(searchLower) ||
      payout.carrier_email?.toLowerCase().includes(searchLower) ||
      payout.pickup_city?.toLowerCase().includes(searchLower) ||
      payout.delivery_city?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-payouts-page">
      <div>
        <h1 className="font-['Barlow_Condensed'] text-3xl font-bold text-slate-900 uppercase tracking-tight">
          Gestion des versements
        </h1>
        <p className="text-slate-600 mt-1">Gérez les paiements aux cotransporteurs</p>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center">
                  <Clock className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-amber-700">{formatPrice(stats.pending_total)}</p>
                  <p className="text-sm text-amber-600">{stats.pending_count} en attente</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-green-200 bg-green-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-700">{formatPrice(stats.completed_total)}</p>
                  <p className="text-sm text-green-600">{stats.completed_count} versés</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-orange-200 bg-orange-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-orange-700">{formatPrice(stats.total_commission_earned)}</p>
                  <p className="text-sm text-orange-600">Commission gagnée</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-slate-700 rounded-lg flex items-center justify-center">
                  <Wallet className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{formatPrice(stats.pending_total + stats.completed_total)}</p>
                  <p className="text-sm text-slate-600">Total traité</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Rechercher par nom, email, ville..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-full md:w-48">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filtrer par statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les versements</SelectItem>
                <SelectItem value="pending">En attente</SelectItem>
                <SelectItem value="processing">En cours</SelectItem>
                <SelectItem value="completed">Versés</SelectItem>
                <SelectItem value="cancelled">Annulés</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchData} className="border-slate-200">
              <RefreshCcw className="w-4 h-4 mr-2" />
              Actualiser
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Payouts List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="spinner mx-auto"></div>
          <p className="text-slate-500 mt-4">Chargement...</p>
        </div>
      ) : filteredPayouts.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="p-12 text-center">
            <Wallet className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500 text-lg">Aucun versement trouvé</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredPayouts.map((payout) => (
            <Card key={payout.id} className="border-slate-200 hover:shadow-md transition-shadow" data-testid={`payout-${payout.id}`}>
              <CardContent className="p-6">
                <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                  {/* Carrier Info */}
                  <div className="flex items-center gap-4 flex-1">
                    <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center">
                      <User className="w-6 h-6 text-slate-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">{payout.carrier_name}</p>
                      <p className="text-sm text-slate-500 flex items-center gap-1">
                        <Mail className="w-3 h-3" />
                        {payout.carrier_email}
                      </p>
                      {payout.carrier_iban ? (
                        <p className="text-xs text-blue-600 font-mono flex items-center gap-1 mt-0.5">
                          <Building className="w-3 h-3" />
                          {payout.carrier_iban}
                        </p>
                      ) : (
                        <p className="text-xs text-amber-600 flex items-center gap-1 mt-0.5">
                          ⚠️ IBAN non renseigné
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Route */}
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <span className="font-medium">{payout.pickup_city}</span>
                    <ArrowRight className="w-4 h-4" />
                    <span className="font-medium">{payout.delivery_city}</span>
                  </div>

                  {/* Amount */}
                  <div className="text-right">
                    <p className="text-2xl font-bold text-green-600">{formatPrice(payout.amount)}</p>
                    <p className="text-xs text-slate-500">
                      Commission: {formatPrice(payout.platform_commission)}
                    </p>
                  </div>

                  {/* Status & Actions */}
                  <div className="flex items-center gap-3">
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border ${getStatusColor(payout.status)}`}>
                      {getStatusIcon(payout.status)}
                      {getStatusLabel(payout.status)}
                    </span>
                    
                    {payout.status === 'pending' && (
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          className="bg-green-500 hover:bg-green-600"
                          onClick={() => openConfirmDialog(payout, 'completed')}
                          data-testid={`complete-btn-${payout.id}`}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Verser
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="border-red-200 text-red-600 hover:bg-red-50"
                          onClick={() => openConfirmDialog(payout, 'cancelled')}
                        >
                          <XCircle className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Additional Info */}
                <div className="mt-4 pt-4 border-t border-slate-100 flex flex-wrap gap-4 text-sm text-slate-500">
                  <span>Livré le: {formatDate(payout.delivered_at)}</span>
                  {payout.completed_at && (
                    <span>Versé le: {formatDate(payout.completed_at)}</span>
                  )}
                  {payout.admin_notes && (
                    <span className="italic">Note: {payout.admin_notes}</span>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Confirm Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {newStatus === 'completed' ? 'Confirmer le versement' : 'Annuler le versement'}
            </DialogTitle>
            <DialogDescription>
              {newStatus === 'completed' 
                ? `Confirmez que vous avez effectué le versement de ${formatPrice(selectedPayout?.amount)} à ${selectedPayout?.carrier_name}.`
                : `Voulez-vous vraiment annuler ce versement de ${formatPrice(selectedPayout?.amount)}?`
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-4 bg-slate-50 rounded-lg">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <span className="text-slate-500">Bénéficiaire:</span>
                <span className="font-medium">{selectedPayout?.carrier_name}</span>
                <span className="text-slate-500">Email:</span>
                <span>{selectedPayout?.carrier_email}</span>
                <span className="text-slate-500">Montant:</span>
                <span className="font-bold text-green-600">{formatPrice(selectedPayout?.amount)}</span>
              </div>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Note (optionnelle)</label>
              <Textarea
                placeholder="Ex: Virement effectué le..., Référence: ABC123"
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleUpdateStatus}
              disabled={updating}
              className={newStatus === 'completed' ? 'bg-green-500 hover:bg-green-600' : 'bg-red-500 hover:bg-red-600'}
            >
              {updating ? 'Traitement...' : (newStatus === 'completed' ? 'Confirmer le versement' : 'Annuler le versement')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPayoutsPage;
